import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
import { useLocation } from 'react-router-dom';
import { PlusCircle, LayoutGrid, BarChart2, List, Table, PieChart, CalendarDays, Zap, Download, Upload, Search, Filter, X, FileText, Users, CalendarRange, GraduationCap, Menu, Plus, Check, Copy, Star, Trash2, Edit3, MoreHorizontal } from 'lucide-react';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { createTask, createRequestForm, createView, updateView, deleteView, duplicateView, setUserDefaultView } from '../services/firestore';
import AutomationRules from './AutomationRules';
import toast from 'react-hot-toast';
import HxSelect from '../../../components/ui/HxSelect';

const VIEW_TYPE_ICONS = {
    lista: List, tablero: LayoutGrid, gantt: BarChart2, panel: PieChart,
    calendario: CalendarDays, carga: Users, semanal: CalendarRange, cursos: GraduationCap, notas: FileText,
};

const VIEW_TYPE_LABELS = {
    lista: 'Lista', tablero: 'Tablero', gantt: 'Gantt', panel: 'Panel',
    calendario: 'Calendario', carga: 'Carga', semanal: 'Semanal', cursos: 'Cursos', notas: 'Notas',
};

const TopBar = ({ onMenuToggle }) => {
    const { activeView, setActiveView, views, activeViewId, setActiveViewId, tasks, selectedProjectId, selectedFolderId, selectedListId, searchQuery, setSearchQuery, filters, setFilters, hasActiveFilters, clearFilters, users } = useTask();
    const { user } = useAuth();
    const location = useLocation();
    const [showAutomations, setShowAutomations] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [tabContextMenu, setTabContextMenu] = useState(null); // { view, x, y }
    const [renamingTabId, setRenamingTabId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [showAddView, setShowAddView] = useState(false);
    const addViewBtnRef = useRef(null);
    const searchRef = useRef(null);
    const fileInputRef = useRef(null);

    const handleExportCSV = () => {
        if (!tasks || tasks.length === 0) { toast('No hay tareas para exportar'); return; }
        const headers = ['Titulo', 'Estado', 'Prioridad', 'Asignados', 'Fecha Vencimiento', 'Descripcion'];
        const rows = tasks.map(t => [
            `"${(t.titulo || '').replace(/"/g, '""')}"`,
            t.estado || '',
            t.prioridad || '',
            (t.asignados || []).join(';'),
            t.fechaVencimiento ? (typeof t.fechaVencimiento === 'object' && t.fechaVencimiento.seconds ? new Date(t.fechaVencimiento.seconds * 1000).toISOString().split('T')[0] : t.fechaVencimiento) : '',
            `"${(t.descripcion || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
        ]);
        const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tareas_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`${tasks.length} tareas exportadas`);
    };

    const handleImportCSV = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) return;
        const text = await file.text();
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) { toast.error('CSV vacio o invalido'); return; }
        let imported = 0;
        for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].match(/(".*?"|[^,]+)/g) || [];
            const titulo = (cols[0] || '').replace(/^"|"$/g, '').replace(/""/g, '"').trim();
            if (!titulo) continue;
            const estado = (cols[1] || 'planificacion').trim();
            const prioridad = (cols[2] || 'media').trim();
            const asignados = (cols[3] || '').split(';').filter(Boolean);
            const fecha = (cols[4] || '').trim();
            const descripcion = (cols[5] || '').replace(/^"|"$/g, '').replace(/""/g, '"').trim();
            try {
                await createTask(user.uid, selectedProjectId, selectedFolderId, selectedListId, {
                    titulo,
                    estado: ['planificacion', 'en-curso', 'finalizado'].includes(estado) ? estado : 'planificacion',
                    prioridad: ['baja', 'media', 'alta'].includes(prioridad) ? prioridad : 'media',
                    asignados,
                    fechaVencimiento: fecha || null,
                    descripcion,
                });
                imported++;
            } catch { /* skip bad rows */ }
        }
        toast.success(`${imported} tareas importadas`);
        e.target.value = '';
    };

    // Show tabs only on list pages
    const showTabs = location.pathname.includes('/lista/');

    // On non-list pages, show only a mobile hamburger
    if (!showTabs) {
        return (
            <div className="md:hidden h-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center px-3 sticky top-0 z-20">
                <button onClick={onMenuToggle} className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition">
                    <Menu className="w-4 h-4" />
                </button>
                <span className="ml-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Hingenia</span>
            </div>
        );
    }

    return (
        <>
        <div className="min-h-[40px] bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-2 sm:px-4 sticky top-0 z-20 transition-colors gap-1">
            <div className="flex items-center gap-1 min-w-0 flex-1">
                {/* Mobile Hamburger */}
                <button
                    onClick={onMenuToggle}
                    className="md:hidden p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition shrink-0"
                    title="Menu"
                >
                    <Menu className="w-4 h-4" />
                </button>

                {/* View Tabs - dynamic from Firestore */}
                <div className="flex items-center gap-0.5 sm:gap-1 overflow-x-auto scrollbar-hide min-w-0 flex-1">
                    {(views.length > 0 ? views : Object.entries(VIEW_TYPE_LABELS).map(([type, label], i) => ({ id: type, name: label, type, orden: i }))).map(view => {
                        const Icon = VIEW_TYPE_ICONS[view.type] || List;
                        const isActive = views.length > 0 ? activeViewId === view.id : activeView === view.type;
                        const isRenaming = renamingTabId === view.id;
                        return (
                            <button
                                key={view.id}
                                onClick={() => {
                                    if (views.length > 0) setActiveViewId(view.id);
                                    else setActiveView(view.type);
                                }}
                                onContextMenu={(e) => {
                                    if (views.length === 0) return;
                                    e.preventDefault();
                                    setTabContextMenu({ view, x: e.clientX, y: e.clientY });
                                }}
                                className={`flex items-center gap-1 px-1.5 sm:px-2 py-1.5 rounded-lg transition text-[11px] sm:text-xs font-medium whitespace-nowrap shrink-0 ${
                                    isActive
                                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-b-2 border-blue-500'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                                title={view.name}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                {isRenaming ? (
                                    <input
                                        value={renameValue}
                                        onChange={(e) => setRenameValue(e.target.value)}
                                        onBlur={async () => {
                                            if (renameValue.trim() && renameValue !== view.name) {
                                                await updateView(user.uid, selectedProjectId, selectedFolderId, selectedListId, view.id, { name: renameValue.trim() });
                                            }
                                            setRenamingTabId(null);
                                        }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setRenamingTabId(null); }}
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                        className="w-20 text-[11px] bg-white border border-blue-400 rounded px-1 py-0 outline-none"
                                    />
                                ) : (
                                    <span className="hidden md:inline">{view.name}</span>
                                )}
                            </button>
                        );
                    })}
                    {/* Add view button */}
                    {views.length > 0 && (
                        <div className="relative">
                            <button
                                ref={addViewBtnRef}
                                onClick={() => setShowAddView(!showAddView)}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors shrink-0"
                                title="Agregar vista"
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </button>
                            {showAddView && createPortal(
                                <div className="fixed z-[99999]" style={{
                                    top: (addViewBtnRef.current?.getBoundingClientRect().bottom || 0) + 4,
                                    left: addViewBtnRef.current?.getBoundingClientRect().left || 0,
                                }}>
                                    <div className="bg-white rounded-xl shadow-2xl border border-gray-200 py-2 w-52 animate-in fade-in zoom-in-95 duration-100"
                                        onClick={() => setShowAddView(false)}>
                                        <div className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase">Agregar vista</div>
                                        {Object.entries(VIEW_TYPE_LABELS).map(([type, label]) => {
                                            const VIcon = VIEW_TYPE_ICONS[type];
                                            return (
                                                <button key={type}
                                                    onClick={async () => {
                                                        const count = views.filter(v => v.type === type).length;
                                                        await createView(user.uid, selectedProjectId, selectedFolderId, selectedListId, {
                                                            name: count > 0 ? `${label} ${count + 1}` : label,
                                                            type, filters: {}, createdBy: user.uid,
                                                        });
                                                        toast.success(`Vista "${label}" agregada`);
                                                    }}
                                                    className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 transition-colors">
                                                    <VIcon className="w-4 h-4 text-gray-500" />
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>,
                                document.body
                            )}
                        </div>
                    )}
                </div>
                {/* Tab Context Menu */}
                {tabContextMenu && createPortal(
                    <div className="fixed inset-0 z-[99998]" onClick={() => setTabContextMenu(null)} onContextMenu={(e) => { e.preventDefault(); setTabContextMenu(null); }}>
                        <div className="fixed z-[99999] bg-white rounded-xl shadow-2xl border border-gray-200 py-2 w-52 animate-in fade-in zoom-in-95 duration-100"
                            style={{ top: tabContextMenu.y, left: Math.min(tabContextMenu.x, window.innerWidth - 220) }}
                            onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => { setRenamingTabId(tabContextMenu.view.id); setRenameValue(tabContextMenu.view.name); setTabContextMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50"><Edit3 className="w-4 h-4 text-gray-400" /> Renombrar</button>
                            <button onClick={async () => { await setUserDefaultView(user.uid, selectedListId, tabContextMenu.view.id); toast.success('Vista predeterminada establecida'); setTabContextMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50"><Star className="w-4 h-4 text-amber-400" /> Establecer como predeterminado</button>
                            <button onClick={async () => { await duplicateView(user.uid, selectedProjectId, selectedFolderId, selectedListId, tabContextMenu.view); toast.success('Vista copiada'); setTabContextMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50"><Copy className="w-4 h-4 text-gray-400" /> Hacer una copia</button>
                            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/tareas/lista/${selectedListId}?view=${tabContextMenu.view.id}`); toast.success('Enlace copiado'); setTabContextMenu(null); }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50"><Copy className="w-4 h-4 text-gray-400" /> Copiar enlace</button>
                            <div className="my-1 border-t border-gray-100" />
                            <button onClick={async () => {
                                if (views.length <= 1) { toast.error('No se puede eliminar la última vista'); setTabContextMenu(null); return; }
                                await deleteView(user.uid, selectedProjectId, selectedFolderId, selectedListId, tabContextMenu.view.id);
                                toast.success('Vista eliminada'); setTabContextMenu(null);
                            }} className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /> Eliminar</button>
                        </div>
                    </div>,
                    document.body
                )}
            </div>

            <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                {/* Search */}
                <div className="relative flex items-center">
                    <Search className="absolute left-2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                    <input
                        ref={searchRef}
                        data-action="search-input"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Buscar..."
                        className="w-24 sm:w-36 lg:w-48 pl-7 pr-7 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 transition-all"
                    />
                    {searchQuery && (
                        <button onClick={() => setSearchQuery('')} className="absolute right-1.5 text-gray-400 hover:text-gray-600">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                {/* Filter Toggle */}
                <div className="relative">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${hasActiveFilters ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                        title="Filtros avanzados"
                    >
                        <Filter className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Filtros</span>
                        {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                    </button>

                    {showFilters && (
                        <div className="absolute right-0 top-full mt-1 w-[calc(100vw-1rem)] sm:w-72 max-w-xs bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 p-3 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Filtros avanzados</span>
                                {hasActiveFilters && (
                                    <button onClick={clearFilters} className="text-[10px] text-red-500 hover:text-red-700 font-medium">Limpiar todo</button>
                                )}
                            </div>

                            {/* Estado */}
                            <div>
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Estado</label>
                                <div className="flex gap-1 mt-1">
                                    {[{ v: 'planificacion', l: 'Planificación', c: 'bg-gray-100 text-gray-700' }, { v: 'en-curso', l: 'En Curso', c: 'bg-blue-100 text-blue-700' }, { v: 'finalizado', l: 'Finalizado', c: 'bg-green-100 text-green-700' }].map(s => (
                                        <button
                                            key={s.v}
                                            onClick={() => setFilters(f => ({ ...f, estado: f.estado === s.v ? null : s.v }))}
                                            className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all ${filters.estado === s.v ? s.c + ' ring-1 ring-offset-1 ring-current' : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100'}`}
                                        >{s.l}</button>
                                    ))}
                                </div>
                            </div>

                            {/* Prioridad */}
                            <div>
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Prioridad</label>
                                <div className="flex gap-1 mt-1">
                                    {[{ v: 'alta', l: 'Alta', c: 'bg-red-100 text-red-700' }, { v: 'media', l: 'Media', c: 'bg-amber-100 text-amber-700' }, { v: 'baja', l: 'Baja', c: 'bg-blue-100 text-blue-700' }].map(p => (
                                        <button
                                            key={p.v}
                                            onClick={() => setFilters(f => ({ ...f, prioridad: f.prioridad === p.v ? null : p.v }))}
                                            className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all ${filters.prioridad === p.v ? p.c + ' ring-1 ring-offset-1 ring-current' : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100'}`}
                                        >{p.l}</button>
                                    ))}
                                </div>
                            </div>

                            {/* Asignado */}
                            <div>
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Asignado a</label>
                                <div className="mt-1"><HxSelect
                                    className="full"
                                    size="sm"
                                    minWidth={0}
                                    value={filters.asignado || ''}
                                    onChange={v => setFilters(f => ({ ...f, asignado: v || null }))}
                                    options={[
                                        { value: '', label: 'Todos', icon: Users },
                                        ...(users || []).slice().sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')).map(u => ({
                                    value: u.email, label: u.displayName || u.email, sub: u.displayName ? u.email : undefined, photo: u.photoURL || undefined, color: '#0a5cff',
                                })),
                                    ]}
                                    placeholder="Todos"
                                    searchPlaceholder="Buscar persona…"
                                    icon={Users}
                                    ariaLabel="Asignado a"
                                /></div>
                            </div>

                            {/* Rango de fechas */}
                            <div>
                                <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Fecha de vencimiento</label>
                                <div className="flex gap-2 mt-1">
                                    <input
                                        type="date"
                                        value={filters.fechaDesde || ''}
                                        onChange={(e) => setFilters(f => ({ ...f, fechaDesde: e.target.value || null }))}
                                        className="flex-1 px-2 py-1.5 text-[10px] border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    />
                                    <span className="text-gray-400 text-[10px] self-center">a</span>
                                    <input
                                        type="date"
                                        value={filters.fechaHasta || ''}
                                        onChange={(e) => setFilters(f => ({ ...f, fechaHasta: e.target.value || null }))}
                                        className="flex-1 px-2 py-1.5 text-[10px] border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Request Form */}
                <button
                    onClick={async () => {
                        if (!user?.uid || !selectedProjectId || !selectedFolderId || !selectedListId) {
                            toast.error('Selecciona una lista primero');
                            return;
                        }
                        try {
                            const formId = await createRequestForm(user.uid, selectedProjectId, selectedFolderId, selectedListId, {
                                name: 'Formulario de solicitud',
                                description: 'Envía una solicitud que se convertirá en tarea',
                            });
                            const url = `${window.location.origin}/formulario/${formId}`;
                            await navigator.clipboard.writeText(url);
                            toast.success('Formulario creado. URL copiada al portapapeles.');
                        } catch {
                            toast.error('Error al crear formulario');
                        }
                    }}
                    className="hidden md:flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                    title="Crear formulario de solicitud público"
                >
                    <FileText className="w-3.5 h-3.5" />
                    <span className="hidden lg:inline">Formulario</span>
                </button>

                {/* Automations */}
                <button
                    onClick={() => setShowAutomations(true)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                    title="Automatizaciones"
                >
                    <Zap className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Automatizar</span>
                </button>
            </div>
        </div>

        {showAutomations && <AutomationRules onClose={() => setShowAutomations(false)} />}
        </>
    );
};

export default TopBar;
